import { useEffect, useRef, useState } from 'react';
import { Box, Button, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import { COR, VARIAVEIS_GRAFICO } from '@/components/graficos/base';
import { fmt } from '@/features/notas/calculo';
import type { DisciplinaDesempenho } from '@/features/notas/types';

/**
 * PAOO · Radar Celeste (microsserviços): cada parte da nota é um serviço que publica um evento no barramento; a consulta
 * recebe os eventos e monta a P1. Abaixo, um radar com a turma em pontos anônimos e você em destaque.
 */

interface Servico {
  id: string;
  nome: string;
  porta: number;
  x: number;
  y: number;
}

const SERVICOS: Servico[] = [
  { id: 'aws', nome: 'aws', porta: 4000, x: 15, y: 18 },
  { id: 'individual', nome: 'individual', porta: 4100, x: 50, y: 18 },
  { id: 'grupo', nome: 'grupo', porta: 4200, x: 85, y: 18 },
  { id: 'barramento', nome: 'barramento-de-eventos', porta: 10000, x: 50, y: 52 },
  { id: 'consulta', nome: 'consulta', porta: 4300, x: 50, y: 86 },
];

type Passo = { de: string; para: string; log: string[] };

function passos(d: DisciplinaDesempenho): Passo[] {
  const partes = [
    { srv: 'aws', comp: 'T1', nota: d.notas.T1 ?? 0, peso: 0.3 },
    { srv: 'individual', comp: 'IND1', nota: d.notas.IND1 ?? 0, peso: 0.5 },
    { srv: 'grupo', comp: 'GRU1', nota: d.notas.GRU1 ?? 0, peso: 0.2 },
  ];
  const out: Passo[] = [];
  let acumulado = 0;
  for (const p of partes) {
    acumulado += p.peso * p.nota;
    const evento = JSON.stringify(
      { tipo: 'NotaLancada', dados: { componente: p.comp, nota: p.nota, peso: p.peso } },
      null,
      2,
    );
    out.push({
      de: p.srv,
      para: 'barramento',
      log: [
        `[${p.srv}:${SERVICOS.find((s) => s.id === p.srv)!.porta}] POST http://localhost:10000/eventos`,
        evento,
      ],
    });
    out.push({
      de: 'barramento',
      para: 'consulta',
      log: [
        `[barramento:10000] repassando NotaLancada para http://localhost:4300/eventos`,
        `[consulta:4300] P1 parcial = ${fmt(acumulado)} · { msg: "ok" }`,
      ],
    });
  }
  out.push({
    de: 'consulta',
    para: 'consulta',
    log: [
      '> GET http://localhost:4300/notas',
      JSON.stringify(
        { P1: d.notas.P1, T1: d.notas.T1, IND1: d.notas.IND1, GRU1: d.notas.GRU1 },
        null,
        2,
      ),
    ],
  });
  return out;
}

function Radar({ d }: { d: DisciplinaDesempenho }) {
  const est = d.turmaInfo.componentes.P1;
  const voce = d.notas.P1 ?? 0;
  const R = 130;
  const raio = (v: number) => ((10 - Math.max(0, Math.min(10, v))) / 10) * R;
  // pontos anônimos: cada aluno da faixa i..i+1 vira um ponto com ângulo pelo "ângulo de ouro" (sem ligação com quem é)
  const pontos: { x: number; y: number }[] = [];
  let k = 0;
  est?.histograma.forEach((n, i) => {
    for (let j = 0; j < n; j++) {
      const ang = (k++ * 137.508 * Math.PI) / 180;
      const r = raio(i + 0.5 + ((j % 3) - 1) * 0.25);
      pontos.push({ x: Math.cos(ang) * r, y: Math.sin(ang) * r });
    }
  });
  const perto = est
    ? est.histograma.filter((_, i) => Math.abs(i + 0.5 - voce) <= 1).reduce((s, n) => s + n, 0)
    : 0;
  return (
    <Box sx={[VARIAVEIS_GRAFICO, { textAlign: 'center' }]}>
      <svg
        viewBox="-150 -150 300 300"
        width="100%"
        style={{ maxWidth: 340 }}
        role="img"
        aria-label={`Radar da P1: você em ${fmt(voce, 1)}, a turma em pontos anônimos`}
      >
        <defs>
          <linearGradient id="varredura" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="var(--g-voce)" stopOpacity="0" />
            <stop offset="1" stopColor="var(--g-voce)" stopOpacity="0.35" />
          </linearGradient>
        </defs>
        {[2, 4, 6, 8].map((n) => (
          <g key={n}>
            <circle r={raio(n)} fill="none" stroke={COR.grade} strokeWidth={1} />
            <text x={4} y={-raio(n) + 12} fontSize={10} fill={COR.textoFraco}>
              {n}
            </text>
          </g>
        ))}
        <circle r={R} fill="none" stroke={COR.turmaForte} strokeWidth={1} />
        <text x={4} y={-R + 12} fontSize={10} fill={COR.textoFraco}>
          0
        </text>
        <Box
          component="g"
          sx={{
            transformOrigin: '0 0',
            animation: 'girar 4s linear infinite',
            '@keyframes girar': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' },
            },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        >
          <path
            d={`M0,0 L${R},0 A${R},${R} 0 0,0 ${R * Math.cos(-0.6)},${R * Math.sin(-0.6)} Z`}
            fill="url(#varredura)"
          />
        </Box>
        {pontos.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={3.5} fill={COR.turmaForte} opacity={0.7} />
        ))}
        <circle
          cx={0}
          cy={-raio(voce)}
          r={7}
          fill={COR.voce}
          stroke={COR.superficie}
          strokeWidth={2}
        />
        <text x={10} y={-raio(voce) + 4} fontSize={12} fontWeight={700} fill={COR.texto}>
          você · {fmt(voce, 1)}
        </text>
        <circle r={3} fill={COR.texto} />
      </svg>
      <Typography variant="body2" color="text.secondary">
        O centro é a nota 10; cada anel vale 2 pontos. Cada ponto cinza é um colega, sem nome (a
        posição mostra só a faixa da nota). {perto}{' '}
        {perto === 1
          ? 'pessoa da turma, contando você, está'
          : 'pessoas da turma, contando você, estão'}{' '}
        a até 1 ponto da sua P1.
      </Typography>
    </Box>
  );
}

export function Barramento({ d }: { d: DisciplinaDesempenho }) {
  const roteiro = passos(d);
  const [i, setI] = useState(-1);
  const [log, setLog] = useState<string[]>([
    '$ docker compose up',
    'aws, individual, grupo, barramento-de-eventos e consulta no ar.',
  ]);
  const timer = useRef<number | null>(null);

  useEffect(() => () => void (timer.current && window.clearInterval(timer.current)), []);

  const dispara = () => {
    if (timer.current) window.clearInterval(timer.current);
    setI(-1);
    setLog([
      '$ docker compose up',
      'aws, individual, grupo, barramento-de-eventos e consulta no ar.',
    ]);
    let k = 0;
    timer.current = window.setInterval(() => {
      if (k >= roteiro.length) {
        window.clearInterval(timer.current!);
        timer.current = null;
        return;
      }
      const p = roteiro[k]!;
      setI(k);
      setLog((l) => [...l, ...p.log]);
      k++;
    }, 900);
  };

  const atual = i >= 0 ? roteiro[i] : null;
  const ativo = (id: string) => atual && (atual.de === id || atual.para === id);
  const centro = (id: string) => SERVICOS.find((s) => s.id === id)!;

  return (
    <Stack spacing={2.5}>
      <Card>
        <CardContent>
          <Typography variant="h6" component="h2">
            O barramento de eventos da sua nota
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Como no Radar Celeste: cada parte da nota é um microsserviço que publica um evento no
            barramento, e a consulta junta tudo na P1. Clique para disparar os eventos.
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={[VARIAVEIS_GRAFICO, { position: 'relative' }]}>
                <svg
                  viewBox="0 0 100 100"
                  width="100%"
                  style={{ maxHeight: 300 }}
                  role="img"
                  aria-label="Microsserviços e barramento de eventos"
                >
                  {['aws', 'individual', 'grupo'].map((id) => (
                    <line
                      key={id}
                      x1={centro(id).x}
                      y1={centro(id).y + 6}
                      x2={50}
                      y2={46}
                      stroke={atual?.de === id ? 'var(--g-voce)' : 'var(--g-grade)'}
                      strokeWidth={atual?.de === id ? 1.4 : 0.6}
                    />
                  ))}
                  <line
                    x1={50}
                    y1={58}
                    x2={50}
                    y2={80}
                    stroke={atual?.para === 'consulta' ? 'var(--g-voce)' : 'var(--g-grade)'}
                    strokeWidth={atual?.para === 'consulta' ? 1.4 : 0.6}
                  />
                  {SERVICOS.map((s) => (
                    <g key={s.id}>
                      <rect
                        x={s.x - (s.id === 'barramento' ? 22 : 13)}
                        y={s.y - 6}
                        width={s.id === 'barramento' ? 44 : 26}
                        height={12}
                        rx={3}
                        fill={ativo(s.id) ? 'var(--g-voce)' : 'var(--g-trilho)'}
                        stroke="var(--g-turma-forte)"
                        strokeWidth={0.3}
                      />
                      <text
                        x={s.x}
                        y={s.y - 0.5}
                        textAnchor="middle"
                        fontSize={3.4}
                        fontWeight={700}
                        fill={ativo(s.id) ? '#fff' : 'var(--g-texto)'}
                      >
                        {s.nome}
                      </text>
                      <text
                        x={s.x}
                        y={s.y + 3.8}
                        textAnchor="middle"
                        fontSize={2.8}
                        fill={ativo(s.id) ? '#fff' : 'var(--g-texto-fraco)'}
                      >
                        :{s.porta}
                      </text>
                    </g>
                  ))}
                </svg>
              </Box>
              <Button
                variant="contained"
                startIcon={<PlayArrowRounded />}
                onClick={dispara}
                sx={{ mt: 1 }}
              >
                Disparar os eventos
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  height: 320,
                  overflowY: 'auto',
                  borderRadius: 2,
                  bgcolor: '#140E26',
                  color: '#D4F45E',
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {log.join('\n')}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {d.turmaInfo.componentes.P1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
              Radar da turma
            </Typography>
            <Radar d={d} />
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
